// The workspaces of the active organization: the shelf itself, the caller's own seat and pin in
// it, and who else sits in it. The role vocabulary's writes are `./workspaceRoles`.

import { api, withQuery } from '../client'
import type { components, operations } from '../schema'

export type WorkspaceView = components['schemas']['WorkspaceView']
export type WorkspaceRoleView = components['schemas']['WorkspaceRoleView']
export type CreateWorkspaceRequest = components['schemas']['CreateWorkspaceRequest']
export type UpdateWorkspaceRequest = components['schemas']['UpdateWorkspaceRequest']
export type ReorderWorkspacePinsRequest = components['schemas']['ReorderWorkspacePinsRequest']
export type WorkspacePinsArranged = components['schemas']['WorkspacePinsArranged']
export type WorkspaceTotalsView = components['schemas']['WorkspaceTotalsView']
export type WorkspaceOverviewEntry = components['schemas']['WorkspaceOverviewEntry']
export type WorkspaceMemberView = components['schemas']['WorkspaceMemberView']
export type AddWorkspaceMemberRequest = components['schemas']['AddWorkspaceMemberRequest']
export type UpdateWorkspaceMemberRequest = components['schemas']['UpdateWorkspaceMemberRequest']
export type WorkspaceMembersQuery = NonNullable<operations['workspaces_members_list']['parameters']['query']>

function workspacePath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}`
}

/**
 * Every workspace of the active organization the caller holds a seat in, each with the role they
 * hold there: pinned ones first in the order they arranged, then by name. Not paginated.
 */
export function listWorkspaces(signal?: AbortSignal): Promise<WorkspaceView[]> {
  return api.get<WorkspaceView[]>('/workspaces', undefined, signal)
}

/**
 * What every workspace `listWorkspaces` answers holds between them, **each asset once**: an asset in
 * its own workspace and in the shared pool is counted by both, so adding the list's counters up
 * counts it twice.
 */
export function getWorkspaceTotals(signal?: AbortSignal): Promise<WorkspaceTotalsView> {
  return api.get<WorkspaceTotalsView>('/workspaces/totals', undefined, signal)
}

/**
 * Every workspace of the organization, seated or not, each with its owner, its seat count and when
 * anything was last placed in it — the administrators' list. Needs `tenancy.workspaces.view_all` or
 * `.manage_all`; anybody else is answered 403. The pool first, then by name. Not paginated.
 */
export function listWorkspaceOverview(signal?: AbortSignal): Promise<WorkspaceOverviewEntry[]> {
  return api.get<WorkspaceOverviewEntry[]>('/workspaces/overview', undefined, signal)
}

/** One workspace, with the role the caller holds in it. 403 when they hold no seat there. */
export function getWorkspace(workspaceId: string, signal?: AbortSignal): Promise<WorkspaceView> {
  return api.get<WorkspaceView>(workspacePath(workspaceId), undefined, signal)
}

/**
 * One role of the organization's workspace vocabulary, with the workspace-scoped keys it
 * declares. Keyed by role key — what a seat stores, not a uid — and asked through a workspace the
 * caller holds a seat in.
 */
export function getWorkspaceRole(
  workspaceId: string,
  roleKey: string,
  signal?: AbortSignal,
): Promise<WorkspaceRoleView> {
  return api.get<WorkspaceRoleView>(
    `${workspacePath(workspaceId)}/roles/${encodeURIComponent(roleKey)}`,
    undefined,
    signal,
  )
}

// ---------------------------------------------------------------------------- the shelf ----

/**
 * Make a workspace, with the caller's own `admin` seat in it. A handle left out is folded out of
 * the name; moderation starts from the organization's template. Needs `tenancy.workspaces.create`;
 * 409 when the handle is taken.
 */
export function createWorkspace(request: CreateWorkspaceRequest): Promise<WorkspaceView> {
  return api.post<WorkspaceView>('/workspaces', request)
}

/**
 * Rename a workspace, or change what it is. `icon` and `color` take an explicit `null` to take them
 * away; turning `moderation_enabled` off exempts whatever was waiting. Needs
 * `assets.workspace.manage` there.
 */
export function updateWorkspace(workspaceId: string, request: UpdateWorkspaceRequest): Promise<WorkspaceView> {
  return api.patch<WorkspaceView>(workspacePath(workspaceId), request)
}

/**
 * Retire a workspace: its seats, placements, shares and overrides go with it, and the assets stay.
 * Needs `assets.workspace.delete` there.
 */
export function deleteWorkspace(workspaceId: string): Promise<undefined> {
  return api.del<undefined>(workspacePath(workspaceId))
}

// ----------------------------------------------------------------- the caller's own seat ----

/** Pin a workspace to the caller's sidebar. Idempotent: a second pin keeps the first one's place. */
export function pinWorkspace(workspaceId: string): Promise<undefined> {
  return api.put<undefined>(`${workspacePath(workspaceId)}/pin`)
}

/** Take a workspace off the caller's sidebar. Idempotent. */
export function unpinWorkspace(workspaceId: string): Promise<undefined> {
  return api.del<undefined>(`${workspacePath(workspaceId)}/pin`)
}

/**
 * Arrange the caller's pins: the whole order, never a delta. A workspace named but not pinned is
 * answered in `unpinned` and left alone, rather than pinned by being mentioned.
 */
export function reorderWorkspacePins(request: ReorderWorkspacePinsRequest): Promise<WorkspacePinsArranged> {
  return api.put<WorkspacePinsArranged>('/workspaces/pins', request)
}

/**
 * Give up the caller's seat. Idempotent. 403 for the workspace's owner, who retires it instead;
 * 409 for its last `admin`.
 */
export function leaveWorkspace(workspaceId: string): Promise<undefined> {
  return api.del<undefined>(`${workspacePath(workspaceId)}/membership`)
}

// -------------------------------------------------------------------------------- seats ----

/**
 * Who sits in a workspace, by role, derived seats included (`is_implicit`). **Seats only, and
 * deliberately**: an offer and a seat are different shapes carrying different writes, so what is
 * still pending is `./workspaceInvitations` and is never merged into this list.
 */
export function listWorkspaceMembers(
  workspaceId: string,
  query: WorkspaceMembersQuery = {},
  signal?: AbortSignal,
): Promise<WorkspaceMemberView[]> {
  return api.get<WorkspaceMemberView[]>(withQuery(`${workspacePath(workspaceId)}/members`, query), undefined, signal)
}

/**
 * Seat somebody who is already in the organization, with no invitation and nothing to accept.
 *
 * **The person is named once, by `user_id` or by `email` — exactly one of the two**; neither, or
 * both, is a 400 that says so. `user_id` is a roster row's; the address is matched
 * case-insensitively inside the caller's own platform, because one address can be two accounts
 * under two brands.
 *
 * **An address that names nobody seatable answers exactly the 400 an unknown uid answers**, and
 * that sameness is the point: the route never tells a caller whether an account exists in somebody
 * else's tenant. Inviting an address that is *not* in the organization is the other half of the
 * gesture and is `createWorkspaceInvitation` in `./workspaceInvitations`.
 *
 * 400 for an account that is not a live member of this organization, or a role neither vocabulary
 * carries; 409 when they already hold a seat here. Needs `assets.members.manage`.
 */
export function addWorkspaceMember(
  workspaceId: string,
  request: AddWorkspaceMemberRequest,
): Promise<WorkspaceMemberView> {
  return api.post<WorkspaceMemberView>(`${workspacePath(workspaceId)}/members`, request)
}

/**
 * Move a seat to another role. 403 for the caller's own seat and the owner's. Needs
 * `assets.members.manage`.
 */
export function updateWorkspaceMember(
  workspaceId: string,
  memberId: string,
  request: UpdateWorkspaceMemberRequest,
): Promise<WorkspaceMemberView> {
  return api.patch<WorkspaceMemberView>(
    `${workspacePath(workspaceId)}/members/${encodeURIComponent(memberId)}`,
    request,
  )
}

/** Take a seat away. 403 for the caller's own seat and the owner's. */
export function removeWorkspaceMember(workspaceId: string, memberId: string): Promise<undefined> {
  return api.del<undefined>(`${workspacePath(workspaceId)}/members/${encodeURIComponent(memberId)}`)
}
